<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted } from 'vue';
import { useBranding } from 'shared/composables/useBranding';
import { useLeadsterUrls } from 'dashboard/composables/useLeadsterUrls';
import IntegrationItem from './IntegrationItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const { replaceInstallationName } = useBranding();

const uiFlags = getters['integrations/getUIFlags'];

const integrationList = computed(
  () => getters['integrations/getAppIntegrations'].value
);
const { marketingIntegrationsUrl } = useLeadsterUrls();
const openMarketingIntegrations = () => {
  window.open(marketingIntegrationsUrl.value, '_blank', 'noopener,noreferrer');
};

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div
            class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
          >
            <div class="flex items-start justify-between">
              <div class="flex items-center gap-1 mb-4">
                <img
                  src="https://cdn.neurologic.com.br/dashboard/integrations/zapier.png"
                  alt="Zapier"
                  class="size-7 rounded-sm object-contain"
                />
                <img
                  src="https://cdn.neurologic.com.br/dashboard/integrations/piperun.png"
                  alt="Piperun"
                  class="size-7 rounded-sm object-contain"
                />
                <img
                  src="https://cdn.neurologic.com.br/dashboard/integrations/pardot.png"
                  alt="Pardot"
                  class="size-7 rounded-sm object-contain"
                />
                <img
                  src="https://cdn.neurologic.com.br/dashboard/integrations/webhook.png"
                  alt="Webhook"
                  class="size-7 rounded-sm object-contain"
                />
                <span class="text-sm font-semibold text-n-slate-11">+</span>
              </div>
            </div>
            <div class="flex flex-col m-0 flex-1">
              <div
                class="font-medium mb-2 text-n-slate-12 flex justify-between items-center gap-2"
              >
                <span class="text-base font-semibold">
                  {{ $t('INTEGRATION_APPS.MARKETING_INTEGRATIONS.TITLE') }}
                </span>
                <Button
                  :label="$t('INTEGRATION_APPS.MARKETING_INTEGRATIONS.BUTTON_TEXT')"
                  icon="i-lucide-square-arrow-out-up-right"
                  link
                  @click="openMarketingIntegrations"
                />
              </div>
              <p class="text-n-slate-11">
                {{
                  $t('INTEGRATION_APPS.MARKETING_INTEGRATIONS.DESCRIPTION')
                }}
              </p>
            </div>
          </div>
          <IntegrationItem
            v-for="item in integrationList"
            :id="item.id"
            :key="item.id"
            :logo="item.logo"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
